import api from './api';
import i18n from '@/plugins/i18n';

const state = {
	error: null,
	success: null,
	drivers: [],
	statistics: {},
	loading: false,
	previousStatistics: null,
};

const getters = {};

const mutations = {
	setError(state, { error }) {
		state.error = error;
	},
	setSuccess(state, { success }) {
		state.success = success;
	},

	setDrivers(state, drivers) {
		state.drivers = drivers;
	},

	setStatistics(state, statistics) {
		const newStatistics = {};
		for (const statistic of statistics) {
			newStatistics[statistic.day] = statistic;
		}
		state.statistics = newStatistics;
	},

	setPreviousStatistics(state, statistics) {
		if (statistics && statistics.length) {
			state.previousStatistics = statistics[0];
		} else {
			state.previousStatistics = null;
		}
	},

	setLastStatisticsOfYear(state, statistics) {
		if (statistics && statistics.length) {
			state.lastStatisticsOfYear = statistics[0];
		}
	},

	setLoading(state, loading) {
		state.loading = loading;
	},
};

const actions = {
	async loadDrivers({ commit }) {
		try {
			const drivers = (await api.getDrivers()).data;
			commit('setDrivers', drivers);
		} catch (err) {
			commit('setError', { error: err.response.data.message });
		}
	},

	async loadStatistics({ commit }, { from, to, user }) {
		try {
			const statistics = (await api.loadStatistics(from, to, [user])).data;
			commit('setStatistics', statistics);

			const dayBefore = new Date(from);
			dayBefore.setDate(dayBefore.getDate() - 1);
			const dayBeforeString = dayBefore.toISOString().split('T')[0];
			const previousStatistics = (await api.loadStatistics(dayBeforeString, dayBeforeString, [user])).data;
			commit('setPreviousStatistics', previousStatistics);

			const dateTo = new Date(to);
			const year = dateTo.getFullYear();
			const lastDayOfYearString = new Date(year + '-12-31');
			const lastStatisticsOfYear = (await api.loadStatistics(lastDayOfYearString, lastDayOfYearString, [user])).data;
			commit('setLastStatisticsOfYear', lastStatisticsOfYear);
		} catch (err) {
			commit('setError', { error: err.response.data.message });
		}
	},
	async sendEmailsByMonth({ commit }, month) {
		commit('setLoading', true);
		try {
			const response = await api.sendEmailsByMonth(month);

			if (response.data) {
				const { accepted, blockedByWhitelist } = response.data;

				let message = '';

				if (accepted > 0 && blockedByWhitelist > 0) {
					message = i18n.t('worktimeSummary.monthly_statements.sent_with_blocked', {
						sent: accepted,
						blocked: blockedByWhitelist,
					});
				} else if (accepted > 0) {
					message = i18n.t('worktimeSummary.monthly_statements.sent', {
						count: accepted,
					});
				} else if (blockedByWhitelist > 0) {
					message = i18n.t('worktimeSummary.monthly_statements.blocked_only', {
						count: blockedByWhitelist,
					});
				}

				commit('setSuccess', { success: message });
			}
		} catch (err) {
			commit('setError', { error: err.response?.data.message });
		} finally {
			commit('setLoading', false);
		}
	},
	async sendEmailToUserByMonth({ commit }, { month, id }) {
		commit('setLoading', true);
		try {
			const response = await api.sendEmailToUserByMonth(month, id);

			if (response.data) {
				const { accepted, blockedByWhitelist } = response.data;

				let message = '';

				if (accepted > 0) {
					message = i18n.t('worktimeSummary.monthly_statements.sent', {
						count: accepted,
					});
				} else if (blockedByWhitelist > 0) {
					message = i18n.t('worktimeSummary.monthly_statements.blocked_by_whitelist');
				} else {
					message = i18n.t('worktimeSummary.monthly_statements.not_sent');
				}

				commit('setSuccess', { success: message });
			}
		} catch (err) {
			commit('setError', { error: err.response?.data.message });
		} finally {
			commit('setLoading', false);
		}
	},

	resetError({ commit }) {
		commit('setError', { error: null });
	},
	resetSuccess({ commit }) {
		commit('setSuccess', { success: null });
	},
	resetStatistics({ commit }) {
		commit('setStatistics', []);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
